<template>
  <v-container fluid class="pa-0 pa-md-4 py-12 py-md-4">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-btn icon="mdi-arrow-left" variant="text" @click="goBack" class="mr-2" />
        <div class="d-flex justify-space-between align-center text-h5 font-weight-bold">
          Add Vehicle using AI Feature
        </div>
      </v-card-title>

      <v-card-text>
        <!-- Step: Upload -->
        <div v-if="step === 'upload'" class="pa-6">
          <div class="text-h6 mb-2 text-center">Upload or take photos</div>
          <div class="text-caption text-grey mb-6 text-center">
            Add 1 or more photos. We’ll try to detect a VIN (or Year/Make/Model) before you create the
            vehicle.
          </div>

          <v-row justify="center">
            <v-col cols="12" md="8" lg="7">
              <v-alert v-if="errorMessage" type="warning" density="comfortable" class="mb-4">
                {{ errorMessage }}
              </v-alert>

              <div class="d-flex flex-wrap justify-center mb-4" style="gap: 8px">
                <v-btn variant="outlined" color="primary" @click="triggerUpload">
                  <v-icon start icon="mdi-upload" />
                  Upload photos
                </v-btn>
                <v-btn variant="outlined" color="primary" @click="triggerCamera">
                  <v-icon start icon="mdi-camera" />
                  Take photo
                </v-btn>
              </div>

              <input
                ref="uploadInputRef"
                type="file"
                accept="image/*"
                multiple
                class="d-none"
                @change="handlePhotoSelection"
              />
              <input
                ref="cameraInputRef"
                type="file"
                accept="image/*"
                capture="environment"
                class="d-none"
                @change="handlePhotoSelection"
              />

              <v-card variant="outlined" class="pa-4">
                <div v-if="photos.length === 0" class="text-center text-body-2 text-grey">
                  No photos selected yet.
                </div>
                <div v-else>
                  <v-row>
                    <v-col v-for="p in photos" :key="p.id" cols="12" sm="6" md="4">
                      <v-card variant="outlined">
                        <v-img :src="p.previewUrl" height="140" cover />
                        <v-card-text class="py-3">
                          <v-text-field
                            v-model="p.name"
                            label="Optional name"
                            density="compact"
                            variant="outlined"
                            hide-details
                            :suffix="p.ext ? '.' + p.ext : undefined"
                          />
                          <div class="d-flex justify-end mt-2">
                            <v-btn
                              size="small"
                              icon="mdi-close"
                              variant="text"
                              @click="removePhoto(p.id)"
                              title="Remove"
                            />
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-card>

              <div class="d-flex justify-center mt-6" style="gap: 8px">
                <v-btn variant="outlined" @click="goBack">Cancel</v-btn>
                <v-btn
                  color="primary"
                  :disabled="photos.length === 0 || isBusy"
                  :loading="isBusy"
                  @click="submitForEvaluation"
                >
                  Send to AI for evaluation
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- Step: Evaluating -->
        <div v-else-if="step === 'evaluating'" class="pa-10 text-center">
          <v-progress-circular indeterminate color="primary" size="44" />
          <div class="mt-4 text-body-1">Evaluating photos…</div>
          <div class="text-caption text-grey mt-1">This usually takes a few seconds.</div>
        </div>

        <!-- Step: Select candidate -->
        <div v-else-if="step === 'select'" class="pa-6">
          <div class="text-h6 mb-2 text-center">Select detected identity</div>
          <div class="text-caption text-grey mb-6 text-center">
            We found multiple candidates. Select one to continue.
          </div>

          <v-row justify="center">
            <v-col cols="12" md="7" lg="6">
              <v-alert v-if="errorMessage" type="warning" density="comfortable" class="mb-4">
                {{ errorMessage }}
              </v-alert>

              <v-card variant="outlined" class="pa-4">
                <div class="text-subtitle-2 font-weight-bold mb-2">VIN candidates</div>
                <v-radio-group v-model="selectedVin">
                  <v-radio
                    v-for="c in vinCandidates"
                    :key="c.value"
                    :label="formatCandidateLabel(c)"
                    :value="c.value"
                  />
                </v-radio-group>
              </v-card>

              <div class="d-flex justify-center mt-6" style="gap: 8px">
                <v-btn variant="outlined" @click="backToUpload">Back</v-btn>
                <v-btn color="primary" :disabled="!selectedVin" @click="confirmFromSelection">
                  Continue
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- Step: Confirm / Edit -->
        <div v-else-if="step === 'confirm'" class="pa-6">
          <div class="text-h6 mb-2 text-center">Confirm VIN</div>
          <div class="text-caption text-grey mb-6 text-center">
            Confirm the detected VIN, or edit it manually.
          </div>

          <v-row justify="center">
            <v-col cols="12" md="7" lg="6">
              <v-alert v-if="errorMessage" type="warning" density="comfortable" class="mb-4">
                {{ errorMessage }}
              </v-alert>

              <v-text-field
                v-model="confirmedVin"
                label="VIN"
                variant="outlined"
                :maxlength="17"
                :error-messages="vinError"
                @input="onVinInput"
              />

              <div class="d-flex justify-center mt-2" style="gap: 8px">
                <v-btn variant="outlined" @click="backToUpload">Retake / Upload more</v-btn>
                <v-btn variant="outlined" @click="backToSelection" :disabled="vinCandidates.length <= 1">
                  Back
                </v-btn>
              </div>

              <div class="d-flex justify-center mt-6" style="gap: 8px">
                <v-btn variant="outlined" @click="enterManually">Enter manually</v-btn>
                <v-btn color="primary" :loading="isBusy" :disabled="!canPromote || isBusy" @click="promoteAndFinish">
                  Continue
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- Step: Done -->
        <div v-else-if="step === 'done'" class="pa-6">
          <v-row justify="center">
            <v-col cols="12" md="7" lg="6">
              <v-alert type="success" density="comfortable" class="mb-4">
                VIN confirmed. Photos have been attached to the VIN folder.
              </v-alert>

              <v-card variant="outlined" class="pa-4">
                <div class="text-subtitle-2 font-weight-bold mb-2">Confirmed VIN</div>
                <div class="text-body-1">{{ confirmedVin }}</div>

                <v-divider class="my-4" />

                <div class="text-subtitle-2 font-weight-bold mb-2">Promoted photos</div>
                <div v-if="promotedUrls.length === 0" class="text-body-2 text-grey">
                  No promoted URLs returned.
                </div>
                <v-list v-else density="compact">
                  <v-list-item v-for="u in promotedUrls" :key="u" :title="u" />
                </v-list>
              </v-card>

              <div class="d-flex justify-center mt-6" style="gap: 8px">
                <v-btn variant="outlined" @click="goBack">Back to Inventory</v-btn>
                <v-btn color="primary" @click="continueToManualAdd">Continue to Add Vehicle</v-btn>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, ref } from 'vue'
import { useRouter } from 'vue-router'
import { v4 as uuidv4 } from 'uuid'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { makeAlert } from '@/utils'
import {
  evaluateVehicleIdentity,
  promoteAiEvalImages,
  requestAiEvalUploadUrls,
  uploadFilesToPresignedUrls,
  type VinCandidate,
} from '@/services/aiVehicleIdentity'
import { isValidVin, sanitizeVin } from '@/utils/vin'

type Step = 'upload' | 'evaluating' | 'select' | 'confirm' | 'done'

interface PhotoItem {
  id: string
  file: File
  name: string
  ext?: string
  previewUrl: string
}

const router = useRouter()
const accountSettings = useAccountSettingsStore()
const endpoint = import.meta.env.VITE_ISE_BACKEND_URL

const step = ref<Step>('upload')
const sessionId = ref(uuidv4())
const isBusy = ref(false)

const uploadInputRef = ref<HTMLInputElement | null>(null)
const cameraInputRef = ref<HTMLInputElement | null>(null)

const photos = ref<PhotoItem[]>([])
const uploadedKeys = ref<string[]>([])

const vinCandidates = ref<VinCandidate[]>([])
const selectedVin = ref<string>('')
const confirmedVin = ref<string>('')
const vinError = ref<string>('')
const errorMessage = ref<string>('')

const promotedUrls = ref<string[]>([])

const canPromote = computed(() => {
  const s = sanitizeVin(confirmedVin.value)
  return isValidVin(s)
})

function goBack() {
  router.push({ name: 'Inventory' })
}

function triggerUpload() {
  uploadInputRef.value?.click?.()
}

function triggerCamera() {
  cameraInputRef.value?.click?.()
}

function handlePhotoSelection(e: Event) {
  const input = e.target as HTMLInputElement
  const files = Array.from(input.files || [])
  for (const file of files) {
    const id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`
    const previewUrl = URL.createObjectURL(file)
    const match = file.name.match(/^(.*?)(?:\.([^.]+))?$/)
    const base = (match?.[1] || file.name).replace(/\s+/g, '_')
    const ext = (match?.[2] || '').toLowerCase()
    photos.value.push({ id, file, name: base, ext, previewUrl })
  }
  if (input) input.value = ''
}

function removePhoto(id: string) {
  const idx = photos.value.findIndex((p) => p.id === id)
  if (idx >= 0) {
    URL.revokeObjectURL(photos.value[idx].previewUrl)
    photos.value.splice(idx, 1)
  }
}

function clearPhotos() {
  for (const p of photos.value) URL.revokeObjectURL(p.previewUrl)
  photos.value = []
}

function backToUpload() {
  errorMessage.value = ''
  vinError.value = ''
  vinCandidates.value = []
  selectedVin.value = ''
  confirmedVin.value = ''
  uploadedKeys.value = []
  promotedUrls.value = []
  sessionId.value = uuidv4()
  step.value = 'upload'
}

function backToSelection() {
  if (vinCandidates.value.length <= 1) return
  vinError.value = ''
  errorMessage.value = ''
  step.value = 'select'
}

function formatCandidateLabel(c: VinCandidate): string {
  const conf = typeof c.confidence === 'number' ? ` (${Math.round(c.confidence * 100)}%)` : ''
  return `${c.value}${conf}`
}

function confirmFromSelection() {
  if (!selectedVin.value) return
  confirmedVin.value = selectedVin.value
  onVinInput()
  step.value = 'confirm'
}

function onVinInput() {
  const s = sanitizeVin(confirmedVin.value)
  confirmedVin.value = s
  vinError.value = s && !isValidVin(s) ? 'VIN must be exactly 17 characters (excluding I, O, Q).' : ''
  errorMessage.value = ''
}

async function submitForEvaluation() {
  if (!endpoint) {
    errorMessage.value = 'Missing VITE_ISE_BACKEND_URL'
    return
  }
  const token = String(accountSettings.sessionToken || '').trim()
  if (!token) {
    errorMessage.value = 'Missing session token. Please sign in again.'
    return
  }
  if (photos.value.length === 0) return

  isBusy.value = true
  errorMessage.value = ''
  vinError.value = ''
  step.value = 'evaluating'
  try {
    const fileNames = photos.value.map((p, i) => {
      const ext = p.ext || p.file.name.split('.').pop() || 'jpg'
      const base = String(p.name || `ai_${i + 1}`).trim()
      return `${base}.${ext}`
    })

    const presign = await requestAiEvalUploadUrls({
      endpoint,
      sessionId: sessionId.value,
      imageCount: photos.value.length,
      fileNames,
      sessionToken: token,
    })

    await uploadFilesToPresignedUrls({
      urls: presign.urls,
      files: photos.value.map((p) => p.file),
    })

    uploadedKeys.value = presign.urls.map((u) => u.key)

    const evalRes = await evaluateVehicleIdentity({
      endpoint,
      sessionId: sessionId.value,
      imageKeys: uploadedKeys.value,
      sessionToken: token,
    })

    vinCandidates.value = Array.isArray(evalRes?.vinCandidates) ? evalRes.vinCandidates : []

    if (vinCandidates.value.length === 0) {
      errorMessage.value =
        'We could not detect a VIN from these photos. You can retake/upload more photos or enter manually.'
      step.value = 'confirm'
      confirmedVin.value = ''
      return
    }

    if (vinCandidates.value.length > 1) {
      selectedVin.value = vinCandidates.value[0]?.value || ''
      step.value = 'select'
      return
    }

    // exactly 1
    confirmedVin.value = vinCandidates.value[0].value
    onVinInput()
    step.value = 'confirm'
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e)
    errorMessage.value = msg
    step.value = 'upload'
    makeAlert(msg, 'error')
  } finally {
    isBusy.value = false
  }
}

async function promoteAndFinish() {
  if (!endpoint) return
  const token = String(accountSettings.sessionToken || '').trim()
  if (!token) {
    errorMessage.value = 'Missing session token. Please sign in again.'
    return
  }
  const vin = sanitizeVin(confirmedVin.value)
  if (!isValidVin(vin)) {
    vinError.value = 'VIN must be exactly 17 characters (excluding I, O, Q).'
    return
  }
  if (uploadedKeys.value.length === 0) {
    errorMessage.value = 'No uploaded images found for this session. Please re-evaluate photos.'
    step.value = 'upload'
    return
  }

  isBusy.value = true
  errorMessage.value = ''
  try {
    const fileNames = photos.value.map((p, i) => {
      const ext = p.ext || p.file.name.split('.').pop() || 'jpg'
      const base = String(p.name || `ai_${i + 1}`).trim()
      return `${base}.${ext}`
    })

    const res = await promoteAiEvalImages({
      endpoint,
      sessionId: sessionId.value,
      vin,
      imageKeys: uploadedKeys.value,
      fileNames,
      sessionToken: token,
    })

    promotedUrls.value = (res.promoted || []).map((p) => p.publicUrl).filter(Boolean)
    step.value = 'done'
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e)
    errorMessage.value = msg
    makeAlert(msg, 'error')
  } finally {
    isBusy.value = false
  }
}

function enterManually() {
  router.push({ name: 'AddVehicle' })
}

function continueToManualAdd() {
  // Minimal handoff: prefill the VIN on the standard Add Vehicle screen.
  router.push({ name: 'AddVehicle', query: { vin: sanitizeVin(confirmedVin.value) } })
}

onBeforeUnmount(() => {
  clearPhotos()
})
</script>

